import { useState } from 'react';
import { Bot, Brain, Sparkles, ShieldCheck, Zap, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Copy, Clock, Server, DollarSign, ChevronDown, ChevronUp, TrendingDown, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import AdComponent from '../components/AdComponent';

const Home = () => {
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const providers = [
    { id: 'openai', name: 'OpenAI', icon: Bot, color: '#10A37F', models: 'GPT-4, GPT-3.5, etc.' },
    { id: 'anthropic', name: 'Anthropic', icon: Brain, color: '#D97757', models: 'Claude 3.5, Claude 3, etc.' },
    { id: 'gemini', name: 'Gemini', icon: Sparkles, color: '#4285F4', models: 'Gemini Pro, Flash, etc.' }
  ];

  // Token pricing per 1M tokens (input/output)
  const tokenPricing = {
    'gpt-4': { input: 30, output: 60 },
    'gpt-4-turbo': { input: 10, output: 30 },
    'gpt-3.5-turbo': { input: 0.5, output: 1.5 },
    'claude-3-5-sonnet-20241022': { input: 3, output: 15 },
    'claude-3-opus-20240229': { input: 15, output: 75 },
    'claude-3-sonnet-20240229': { input: 3, output: 15 },
    'claude-3-haiku-20240307': { input: 0.25, output: 1.25 },
    'gemini-pro': { input: 0.5, output: 1.5 },
    'gemini-1.5-pro': { input: 1.25, output: 5 },
    'gemini-1.5-flash': { input: 0.075, output: 0.3 }
  };

  const estimateTokens = (text) => {
    // Rough estimation: 1 token ≈ 4 characters
    return Math.ceil(text.length / 4);
  };

  const calculateCost = (inputTokens, outputTokens, model) => {
    const pricing = tokenPricing[model] || { input: 1, output: 2 };
    const inputCost = (inputTokens / 1000000) * pricing.input;
    const outputCost = (outputTokens / 1000000) * pricing.output;
    return (inputCost + outputCost).toFixed(6);
  };

  const testOpenAIKey = async (key) => {
    const startTime = performance.now();
    const testPrompt = customPrompt || 'Say "Hello" in one word.';
    try {
      // First get models list
      const modelsResponse = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!modelsResponse.ok) {
        const error = await modelsResponse.json();
        const endTime = performance.now();
        return {
          success: false,
          provider: 'OpenAI',
          latency: Math.round(endTime - startTime),
          message: error.error?.message || 'Invalid API key or authentication failed'
        };
      }

      const modelsData = await modelsResponse.json();
      const models = modelsData.data?.slice(0, 10).map(m => m.id) || [];

      // Test with a completion if custom prompt provided
      let usage = null;
      let estimatedCost = null;
      if (customPrompt) {
        try {
          const completionResponse = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${key}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              model: 'gpt-3.5-turbo',
              messages: [{ role: 'user', content: testPrompt }],
              max_tokens: 50
            })
          });

          if (completionResponse.ok) {
            const completionData = await completionResponse.json();
            usage = completionData.usage;
            if (usage) {
              estimatedCost = calculateCost(usage.prompt_tokens, usage.completion_tokens, 'gpt-3.5-turbo');
            }
          }
        } catch (e) {
          // Continue without completion test
        }
      }

      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      return {
        success: true,
        provider: 'OpenAI',
        latency,
        models,
        totalModels: modelsData.data?.length || 0,
        message: 'API key is valid and working!',
        usage,
        estimatedCost
      };
    } catch (error) {
      return {
        success: false,
        provider: 'OpenAI',
        message: `Network error: ${error.message}. Check CORS or network connection.`
      };
    }
  };

  const testAnthropicKey = async (key) => {
    const startTime = performance.now();
    const testPrompt = customPrompt || 'Say "Hello" in one word.';
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': key,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: customPrompt ? 100 : 10,
          messages: [{ role: 'user', content: testPrompt }]
        })
      });
      
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      if (response.ok) {
        const data = await response.json();
        const usage = data.usage;
        const estimatedCost = usage ? calculateCost(usage.input_tokens, usage.output_tokens, 'claude-3-5-sonnet-20241022') : null;

        return {
          success: true,
          provider: 'Anthropic',
          latency,
          models: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
          totalModels: 4,
          message: 'API key is valid and working!',
          usage,
          estimatedCost
        };
      } else {
        const error = await response.json();
        return {
          success: false,
          provider: 'Anthropic',
          latency,
          message: error.error?.message || 'Invalid API key or authentication failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        provider: 'Anthropic',
        message: `Network error: ${error.message}. This may be due to CORS restrictions.`
      };
    }
  };

  const testGeminiKey = async (key) => {
    const startTime = performance.now();
    const testPrompt = customPrompt || 'Say "Hello" in one word.';
    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [{ parts: [{ text: testPrompt }] }]
        })
      });
      
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      if (response.ok) {
        const data = await response.json();
        // Estimate tokens for Gemini (no usage data in response)
        const inputTokens = estimateTokens(testPrompt);
        const outputText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
        const outputTokens = estimateTokens(outputText);
        const estimatedCost = calculateCost(inputTokens, outputTokens, 'gemini-pro');

        return {
          success: true,
          provider: 'Google Gemini',
          latency,
          models: ['gemini-pro', 'gemini-pro-vision', 'gemini-1.5-pro', 'gemini-1.5-flash'],
          totalModels: 4,
          message: 'API key is valid and working!',
          usage: { input_tokens: inputTokens, output_tokens: outputTokens },
          estimatedCost
        };
      } else {
        const error = await response.json();
        return {
          success: false,
          provider: 'Google Gemini',
          latency,
          message: error.error?.message || 'Invalid API key or authentication failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        provider: 'Google Gemini',
        message: `Network error: ${error.message}. Check your API key and network.`
      };
    }
  };

  const handleTestKey = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    // Google Ads conversion tracking (Commented out until you apply for Google Ads)
    /*
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'YOUR-CONVERSION-ID/YOUR-CONVERSION-LABEL',
        'event_callback': () => {}
      });
    }
    */

    setTesting(true);
    setTestResults(null);

    let result;
    if (selectedProvider === 'openai') {
      result = await testOpenAIKey(apiKey);
    } else if (selectedProvider === 'anthropic') {
      result = await testAnthropicKey(apiKey);
    } else if (selectedProvider === 'gemini') {
      result = await testGeminiKey(apiKey);
    }

    setTestResults(result);
    setTesting(false);

    if (result.success) {
      toast.success('API key validated successfully!');
    } else {
      toast.error('API key validation failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero Section */}
      <div className="hero-gradient grid-pattern relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]"></div>
        
        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          {/* Trust Badges */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap" data-testid="trust-badges">
            <Badge variant="outline" className="glass-card px-4 py-2 border-emerald-500/30 text-emerald-400">
              <ShieldCheck className="w-4 h-4 mr-2" data-testid="shield-icon" />
              100% Client-Side
            </Badge>
            <Badge variant="outline" className="glass-card px-4 py-2 border-emerald-500/30 text-emerald-400">
              <Lock className="w-4 h-4 mr-2" data-testid="lock-icon" />
              No Storage
            </Badge>
            <Badge variant="outline" className="glass-card px-4 py-2 border-emerald-500/30 text-emerald-400">
              <Zap className="w-4 h-4 mr-2" data-testid="zap-icon" />
              Instant Results
            </Badge>
          </div>

          {/* Main Heading */}
          <div className="text-center mb-16 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6">
              <img 
                src="/logo.svg" 
                alt="KeyProbe Logo" 
                className="w-12 h-12 md:w-16 md:h-16 object-contain render-crisp" 
                onError={(e) => e.target.style.display = 'none'} 
              />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold bg-gradient-to-r from-emerald-400 to-emerald-200 bg-clip-text text-transparent leading-tight tracking-tight">
                KeyProbe
              </h1>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold mb-6 text-white/90" data-testid="main-heading">
              Test Your LLM API Keys Instantly & Securely
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-testid="hero-description">
              Validate OpenAI, Anthropic Claude, and Google Gemini API keys in seconds. 
              Get real-time connectivity, model availability, and performance metrics. 
              All processing happens in your browser - we never see or store your keys.
            </p>
          </div>

          {/* Provider Selection */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {providers.map((provider) => {
                const Icon = provider.icon;
                return (
                  <Card
                    key={provider.id}
                    data-testid={`provider-card-${provider.id}`}
                    className={`glass-card p-6 cursor-pointer transition-all duration-300 provider-card-${provider.id} ${
                      selectedProvider === provider.id ? 'ring-2 ring-emerald-500 glow-emerald' : ''
                    }`}
                    onClick={() => setSelectedProvider(provider.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${provider.color}20` }}>
                        <Icon className="w-6 h-6" style={{ color: provider.color }} data-testid={`${provider.id}-icon`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg mb-1">{provider.name}</h3>
                        <p className="text-sm text-gray-500">{provider.models}</p>
                      </div>
                      {selectedProvider === provider.id && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" data-testid={`${provider.id}-selected`} />
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* API Key Input */}
            <Card className="glass-card p-6 input-glow">
              <label className="block text-sm font-medium text-gray-400 mb-3" data-testid="api-key-label">
                Enter your {providers.find(p => p.id === selectedProvider)?.name} API Key
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-proj-..." 
                    data-testid="api-key-input"
                    className="w-full h-14 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 text-lg font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                    onKeyDown={(e) => e.key === 'Enter' && handleTestKey()}
                  />
                  <button
                    onClick={() => setShowKey(!showKey)}
                    data-testid="toggle-key-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button
                  onClick={handleTestKey}
                  disabled={testing}
                  data-testid="test-key-button"
                  className="h-14 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg glow-emerald test-button transition-all duration-300 active:scale-95 disabled:opacity-50"
                >
                  {testing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Testing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Test Key
                    </span>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-3" data-testid="security-notice">
                <Lock className="w-3 h-3 inline mr-1" />
                Your API key is processed entirely in your browser and never sent to our servers.
              </p>

              {/* Advanced Options */}
              <div className="mt-4 border-t border-[#27272A] pt-4">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  data-testid="toggle-advanced"
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-emerald-500 transition-colors"
                >
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Advanced Options
                </button>
                
                {showAdvanced && (
                  <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="advanced-options">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Custom Test Prompt (optional)
                      </label>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Enter a custom prompt to test with your API key..."
                        data-testid="custom-prompt-input"
                        rows={3}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all resize-none"
                      />
                      <p className="text-xs text-gray-600 mt-2">
                        Leave empty to use default validation. Custom prompts will test actual API responses and calculate token costs.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Ad Unit Below Input */}
          <AdComponent adSlot="7890123456" />

          {/* Results Dashboard */}
          {testResults && (
            <div className="max-w-5xl mx-auto animate-in fade-in zoom-in-95 duration-500" data-testid="results-dashboard">
              <Card className="glass-card p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold mb-6" data-testid="results-heading">Test Results</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {/* Status */}
                  <Card className="bg-[#0A0A0A] border-[#27272A] p-4 metric-card" data-testid="status-card">
                    <div className="flex items-center gap-3">
                      {testResults.success ? (
                        <CheckCircle className="w-8 h-8 text-emerald-500" data-testid="success-icon" />
                      ) : (
                        <AlertCircle className="w-8 h-8 text-red-500" data-testid="error-icon" />
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className={`font-semibold ${testResults.success ? 'text-emerald-500' : 'text-red-500'}`} data-testid="status-text">
                          {testResults.success ? 'Valid' : 'Invalid'}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Latency */}
                  {testResults.latency && (
                    <Card className="bg-[#0A0A0A] border-[#27272A] p-4 metric-card" data-testid="latency-card">
                      <div className="flex items-center gap-3">
                        <Clock className="w-8 h-8 text-blue-500" data-testid="clock-icon" />
                        <div>
                          <p className="text-sm text-gray-500">Latency</p>
                          <p className="font-semibold text-blue-500" data-testid="latency-text">{testResults.latency}ms</p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Provider */}
                  <Card className="bg-[#0A0A0A] border-[#27272A] p-4 metric-card" data-testid="provider-card">
                    <div className="flex items-center gap-3">
                      <Server className="w-8 h-8 text-purple-500" data-testid="server-icon" />
                      <div>
                        <p className="text-sm text-gray-500">Provider</p>
                        <p className="font-semibold text-purple-500" data-testid="provider-text">{testResults.provider}</p>
                      </div>
                    </div>
                  </Card>

                  {/* Total Models */}
                  {testResults.totalModels && (
                    <Card className="bg-[#0A0A0A] border-[#27272A] p-4 metric-card" data-testid="models-count-card">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-8 h-8 text-yellow-500" data-testid="sparkles-icon" />
                        <div>
                          <p className="text-sm text-gray-500">Models</p>
                          <p className="font-semibold text-yellow-500" data-testid="models-count">{testResults.totalModels}</p>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>

                {/* Token Usage & Cost */}
                {testResults.success && (testResults.usage || testResults.estimatedCost) && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="token-usage-section">
                    {testResults.usage && (
                      <>
                        <Card className="bg-[#0A0A0A] border-[#27272A] p-4" data-testid="input-tokens-card">
                          <p className="text-sm text-gray-500 mb-1">Input Tokens</p>
                          <p className="text-2xl font-semibold text-emerald-500">
                            {testResults.usage.prompt_tokens || testResults.usage.input_tokens || 0}
                          </p>
                        </Card>
                        <Card className="bg-[#0A0A0A] border-[#27272A] p-4" data-testid="output-tokens-card">
                          <p className="text-sm text-gray-500 mb-1">Output Tokens</p>
                          <p className="text-2xl font-semibold text-blue-500">
                            {testResults.usage.completion_tokens || testResults.usage.output_tokens || 0}
                          </p>
                        </Card>
                      </>
                    )}
                    {testResults.estimatedCost && (
                      <Card className="bg-[#0A0A0A] border-[#27272A] p-4" data-testid="estimated-cost-card">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-yellow-500" />
                          <p className="text-sm text-gray-500">Estimated Cost</p>
                        </div>
                        <p className="text-2xl font-semibold text-yellow-500">${testResults.estimatedCost}</p>
                        <p className="text-xs text-gray-600 mt-1">Per request</p>
                      </Card>
                    )}
                  </div>
                )}

                {/* Message */}
                <Card className="bg-[#0A0A0A] border-[#27272A] p-4 mb-6" data-testid="message-card">
                  <p className={`${testResults.success ? 'text-emerald-400' : 'text-red-400'}`} data-testid="result-message">
                    {testResults.message}
                  </p>
                </Card>

                {/* Available Models */}
                {testResults.success && testResults.models && testResults.models.length > 0 && (
                  <Card className="bg-[#0A0A0A] border-[#27272A] p-6" data-testid="models-list-card">
                    <h3 className="font-heading font-semibold mb-4" data-testid="models-list-heading">Available Models</h3>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {testResults.models.map((model, index) => (
                        <div
                          key={index}
                          data-testid={`model-item-${index}`}
                          className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-[#27272A] hover:border-emerald-500/30 transition-colors group"
                        >
                          <code className="text-sm font-mono text-gray-300">{model}</code>
                          <button
                            onClick={() => copyToClipboard(model)}
                            data-testid={`copy-model-${index}`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-emerald-500"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Security Section */}
      <div className="container mx-auto px-4 py-16" data-testid="security-section">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl font-bold mb-4" data-testid="security-heading">Why It's Secure</h2>
          <p className="text-gray-400 max-w-2xl mx-auto" data-testid="security-description">
            We take security seriously. Here's how we protect your API keys.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <Card className="glass-card p-6 text-center" data-testid="security-feature-1">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Client-Side Processing</h3>
            <p className="text-sm text-gray-500">
              All API calls are made directly from your browser to the LLM providers. We never touch your keys.
            </p>
          </Card>

          <Card className="glass-card p-6 text-center" data-testid="security-feature-2">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Zero Storage</h3>
            <p className="text-sm text-gray-500">
              Your API keys are never stored in memory, cookies, or any database. They exist only during testing.
            </p>
          </Card>

          <Card className="glass-card p-6 text-center" data-testid="security-feature-3">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <Zap className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Open Source</h3>
            <p className="text-sm text-gray-500">
              Our code is transparent and verifiable. You can inspect exactly what happens with your keys.
            </p>
          </Card>
        </div>
      </div>

      {/* Pricing Comparison Section */}
      <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent" data-testid="pricing-section">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BarChart3 className="w-8 h-8 text-emerald-500" />
            <h2 className="font-heading text-3xl font-bold" data-testid="pricing-heading">Cost Comparison</h2>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto" data-testid="pricing-description">
            Compare pricing across providers to find the most cost-effective option for your use case.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Pricing Table */}
          <Card className="glass-card overflow-hidden" data-testid="pricing-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#27272A]">
                    <th className="text-left p-4 font-heading font-semibold text-emerald-500">Model</th>
                    <th className="text-left p-4 font-heading font-semibold text-emerald-500">Provider</th>
                    <th className="text-right p-4 font-heading font-semibold text-blue-500">Input<br /><span className="text-xs text-gray-500 font-normal">per 1M tokens</span></th>
                    <th className="text-right p-4 font-heading font-semibold text-purple-500">Output<br /><span className="text-xs text-gray-500 font-normal">per 1M tokens</span></th>
                    <th className="text-right p-4 font-heading font-semibold text-yellow-500">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-1">
                    <td className="p-4 font-mono text-sm">claude-3-haiku</td>
                    <td className="p-4">
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">Anthropic</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$0.25</td>
                    <td className="p-4 text-right text-purple-400">$1.25</td>
                    <td className="p-4 text-right text-sm text-gray-400">Budget</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-2">
                    <td className="p-4 font-mono text-sm">gpt-3.5-turbo</td>
                    <td className="p-4">
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">OpenAI</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$0.50</td>
                    <td className="p-4 text-right text-purple-400">$1.50</td>
                    <td className="p-4 text-right text-sm text-gray-400">Fast & Cheap</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-3">
                    <td className="p-4 font-mono text-sm">gemini-pro</td>
                    <td className="p-4">
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Google</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$0.50</td>
                    <td className="p-4 text-right text-purple-400">$1.50</td>
                    <td className="p-4 text-right text-sm text-gray-400">Balanced</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-4">
                    <td className="p-4 font-mono text-sm">gemini-1.5-flash</td>
                    <td className="p-4">
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Google</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$0.075</td>
                    <td className="p-4 text-right text-purple-400">$0.30</td>
                    <td className="p-4 text-right text-sm text-gray-400">
                      <TrendingDown className="w-4 h-4 inline text-emerald-500 mr-1" />
                      Best Value
                    </td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-5">
                    <td className="p-4 font-mono text-sm">gemini-1.5-pro</td>
                    <td className="p-4">
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Google</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$1.25</td>
                    <td className="p-4 text-right text-purple-400">$5.00</td>
                    <td className="p-4 text-right text-sm text-gray-400">Advanced</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-6">
                    <td className="p-4 font-mono text-sm">claude-3-sonnet</td>
                    <td className="p-4">
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">Anthropic</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$3.00</td>
                    <td className="p-4 text-right text-purple-400">$15.00</td>
                    <td className="p-4 text-right text-sm text-gray-400">Quality</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-7">
                    <td className="p-4 font-mono text-sm">gpt-4-turbo</td>
                    <td className="p-4">
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">OpenAI</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$10.00</td>
                    <td className="p-4 text-right text-purple-400">$30.00</td>
                    <td className="p-4 text-right text-sm text-gray-400">Premium</td>
                  </tr>
                  <tr className="border-b border-[#27272A] hover:bg-emerald-500/5 transition-colors" data-testid="price-row-8">
                    <td className="p-4 font-mono text-sm">claude-3-opus</td>
                    <td className="p-4">
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">Anthropic</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$15.00</td>
                    <td className="p-4 text-right text-purple-400">$75.00</td>
                    <td className="p-4 text-right text-sm text-gray-400">Top Tier</td>
                  </tr>
                  <tr className="hover:bg-emerald-500/5 transition-colors" data-testid="price-row-9">
                    <td className="p-4 font-mono text-sm">gpt-4</td>
                    <td className="p-4">
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">OpenAI</Badge>
                    </td>
                    <td className="p-4 text-right text-blue-400">$30.00</td>
                    <td className="p-4 text-right text-purple-400">$60.00</td>
                    <td className="p-4 text-right text-sm text-gray-400">Enterprise</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* Cost Savings Tips */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <Card className="glass-card p-6" data-testid="tip-card-1">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Best Value</h4>
                  <p className="text-sm text-gray-500">Gemini 1.5 Flash offers the lowest cost per token at $0.075/$0.30</p>
                </div>
              </div>
            </Card>

            <Card className="glass-card p-6" data-testid="tip-card-2">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Budget Friendly</h4>
                  <p className="text-sm text-gray-500">Claude Haiku and GPT-3.5 are great for high-volume, cost-sensitive tasks</p>
                </div>
              </div>
            </Card>

            <Card className="glass-card p-6" data-testid="tip-card-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Premium Quality</h4>
                  <p className="text-sm text-gray-500">GPT-4 and Claude Opus provide best-in-class reasoning for complex tasks</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold mb-8 text-center" data-testid="faq-heading">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <Card className="glass-card p-6" data-testid="faq-item-1">
              <h3 className="font-heading font-semibold mb-2">Is this really free?</h3>
              <p className="text-gray-400 text-sm">Yes, 100% free. No hidden costs, no sign-ups required.</p>
            </Card>

            <Card className="glass-card p-6" data-testid="faq-item-2">
              <h3 className="font-heading font-semibold mb-2">How does client-side testing work?</h3>
              <p className="text-gray-400 text-sm">
                Your browser makes direct API calls to OpenAI, Anthropic, or Google. The keys never pass through our servers.
              </p>
            </Card>

            <Card className="glass-card p-6" data-testid="faq-item-3">
              <h3 className="font-heading font-semibold mb-2">What if I get a CORS error?</h3>
              <p className="text-gray-400 text-sm">
                Some providers have CORS restrictions for browser requests. This doesn't mean your key is invalid - try using it in your backend code.
              </p>
            </Card>

            <Card className="glass-card p-6" data-testid="faq-item-4">
              <h3 className="font-heading font-semibold mb-2">Which providers are supported?</h3>
              <p className="text-gray-400 text-sm">
                Currently: OpenAI (GPT models), Anthropic (Claude), and Google (Gemini). More coming soon!
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Ad Unit Above Footer */}
      <div className="container mx-auto px-4 pb-8">
        <AdComponent adSlot="1234567890" />
      </div>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-8" data-testid="footer">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          <p className="mb-2">© 2026 LLM API Key Tester. Built with security in mind.</p>
          <p className="mb-3">No data collection • No tracking • No storage</p>
          <p className="text-gray-400">
            Made with <span className="text-red-500">❤</span> by{' '}
            <a 
              href="https://www.linkedin.com/in/ankitnarang1/" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="creator-link"
              className="text-emerald-500 hover:text-emerald-400 transition-colors font-medium"
            >
              Ankit Narang
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;